import React, { useState, useEffect, useCallback } from 'react';
import API_BASE_URL from '../api';
import { authFetch } from '../authFetch';
import { useAuth } from '../authContext';
import useSelectedRadio from '../hooks/useSelectedRadio';
import { useTranslation } from 'react-i18next';
import { useLocale, fmtNumber } from '../i18n/format';
import { resolveCountryFlag } from '../constants/countries';
import ExportButton from '../components/ExportButton';
import { buildListenersDoc } from "../pdf/builders/listeners/buildDoc";
import { buildListenersWorkbook } from "../excel/builders/listeners/buildWorkbook";

const normTime = (t) => (t && t.length === 5 ? `${t}:00` : (t || '00:00:00'));



export default function ListenersServicePage() {
  const { radioId, shortcode, name: stationName } = useSelectedRadio();

  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [country, setCountry] = useState('');
  const [countries, setCountries] = useState([]);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { isAuth } = useAuth();
  const { t } = useTranslation('common');
  const locale = useLocale();

  useEffect(() => {
    if (!startDate) {
      const today = new Date();
      setStartDate(today.toISOString().slice(0, 10));
      setEndDate(today.toISOString().slice(0, 10));
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!isAuth || !radioId || !startDate || !endDate) {
      if (!isAuth) setCountries([]);
      return;
    }
    const ST = normTime(startTime);
    const ET = normTime(endTime);

    const API = (API_BASE_URL || '').replace(/\/+$/, '');
    let url = `${API}/radios/${encodeURIComponent(radioId)}/metrics/geo/countries`
            + `?start_date=${startDate}&end_date=${endDate}&start_time=${ST}&end_time=${ET}`;
    authFetch(url)
      .then(r => r.json())
      .then(json => {
        if (json.data) {
          const names = json.data.map((r) => r.country).filter(Boolean);
          const unique = Array.from(new Set(names));
          const mapped = unique.map((name) => ({
            name,
            flag: resolveCountryFlag(name) || '',
          }));
          setCountries(mapped);
        }
      })
      .catch(() => setCountries([]));
  }, [isAuth, radioId, startDate, endDate, startTime, endTime]);

  const fetchRowsAndTotal = useCallback(async () => {
    if (!isAuth) {
      setError('');
      setRows([]);
      setTotal(null);
      return;
    }
    if (!radioId || !startDate || !endDate) {
      setError(t('listeners.errors.select_dates'));
      setRows([]);
      setTotal(null);
      return;
    }
    setError('');
    setLoading(true);
    try {
      const ST = normTime(startTime);
      const ET = normTime(endTime);

      let common = `start_date=${startDate}&end_date=${endDate}&start_time=${ST}&end_time=${ET}`;
      if (country) common += `&country=${encodeURIComponent(country)}`;

      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const base = `${API}/radios/${encodeURIComponent(radioId)}/metrics/listeners`;

      // Все через authFetch:
      const cntResp = await authFetch(`${base}/count?${common}`);
      const cntJson = await cntResp.json();
      if (typeof cntJson.count !== 'number') throw new Error(t('listeners.errors.count_error'));
      setTotal(cntJson.count);

      const rowsResp = await authFetch(`${base}/by-hour?${common}`);
      const rowsJson = await rowsResp.json();
      if (!rowsJson.data) throw new Error(t('listeners.errors.data_error'));
      setRows(rowsJson.data);
    } catch (e) {
      setError(e?.message || t('listeners.errors.generic_error'));
      setRows([]);
      setTotal(null);
    }
    setLoading(false);
  }, [isAuth, radioId, startDate, endDate, startTime, endTime, country, t]);

  const onSubmit = e => {
    e.preventDefault();
    fetchRowsAndTotal();
  };

  // Данные загружаются только после ручного сабмита формы
  if (!radioId) {
    return (
      <div style={widgetStyle}>
        {/* BackToServicesButton removed */}
        <h2 style={{ marginBottom: 8 }}>{t('listeners.title')}</h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          {t('listeners.no_channel')}
        </div>
      </div>
    );
  }

  return (
    <div style={widgetStyle}>
      {/* BackToServicesButton removed */}

      {/* Текстовое поле-описание */}
      <div
        style={{
          width: "100%",
          padding: "8px 12px",
          borderRadius: 8,
          background: "#f8f8f8",
          color: "#1a1a1a",
          fontSize: 17,
          margin: "8px 0 18px 0",
          boxSizing: "border-box"
        }}
      >
        {t('listeners.lead')}
      </div>

      <h2 style={{ marginBottom: 8 }}>🎧 {t('listeners.title')}</h2>
      <form style={filtersFormStyle} onSubmit={onSubmit}>
        <table style={formTableStyle}>
                    <tbody>
            <tr>
              <td style={formLabelCellStyle}>
                <label htmlFor="listeners-start-date" style={{ ...fieldLabelStyle, fontWeight: 400 }}>
                  <span role="img" aria-hidden="true">📅</span> {t('listeners.from_date')}
                </label>
              </td>
              <td style={formControlCellStyle}>
                <div style={dateFieldWrapperStyle}>
                  <input
                    id="listeners-start-date"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={controlStyle}
                  />
                </div>
              </td>

              <td style={formSpacerCellStyle} />

              {/* Час з — уменьшили расстояние ТОЛЬКО здесь */}
              <td colSpan={2} style={{ padding: '6px 0', verticalAlign: 'middle' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 20, alignItems: 'center' }}>
            <label htmlFor="listeners-start-time" style={{ ...fieldLabelStyle, fontWeight: 400 }}>
              <span role="img" aria-hidden="true">🕑</span> {t('listeners.time_from')}
                  </label>
                  <div style={timeFieldWrapperStyle}>
                    <input
                      id="listeners-start-time"
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      style={controlStyle}
                    />
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td style={formLabelCellStyle}>
                <label htmlFor="listeners-end-date" style={{ ...fieldLabelStyle, fontWeight: 400 }}>
                  <span role="img" aria-hidden="true">📅</span> {t('listeners.to_date')}
                </label>
              </td>
              <td style={formControlCellStyle}>
                <div style={dateFieldWrapperStyle}>
                  <input
                    id="listeners-end-date"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={controlStyle}
                  />
                </div>
              </td>

              <td style={formSpacerCellStyle} />

              {/* Час до — уменьшили расстояние ТОЛЬКО здесь */}
              <td colSpan={2} style={{ padding: '6px 0', verticalAlign: 'middle' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', columnGap: 20, alignItems: 'center' }}>
                <label htmlFor="listeners-end-time" style={{ ...fieldLabelStyle, fontWeight: 400 }}>
                  <span role="img" aria-hidden="true">🕑</span> {t('listeners.time_to')}
                  </label>
                  <div style={timeFieldWrapperStyle}>
                    <input
                      id="listeners-end-time"
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                      style={controlStyle}
                    />
                  </div>
                </div>
              </td>
            </tr>

            <tr>
              <td style={formLabelCellStyle}>
                <label htmlFor="listeners-country" style={{ ...fieldLabelStyle, fontWeight: 400 }}>
                  <span role="img" aria-hidden="true">🌍</span> {t('listeners.country')}
                </label>
              </td>
              <td style={formControlCellStyle}>
                <div style={dateFieldWrapperStyle}>
                  <select
                    id="listeners-country"
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    style={controlStyle}
                  >
                  <option value="">{t('listeners.all')}</option>
                    {countries.map(({ name, flag }) => (
                      <option key={name} value={name}>
                        {flag ? `${flag} ${name}` : name}
                      </option>
                    ))}
                  </select>
                </div>
              </td>

              <td style={formSpacerCellStyle} />

              <td colSpan={2} style={buttonCellStyle}>
                <div style={buttonWrapperStyle}>
                  <button type="submit" style={showBtn}>{t('listeners.show')}</button>
                  <ExportButton
                    data={rows}
                    onBeforeBuild={async () => {
                      // грузим логотип из public и конвертим в dataURL
                      const logoUrl = `${process.env.PUBLIC_URL || ''}/Radiomonitor.NetLogo.png`;
                      try {
                        const resp = await fetch(logoUrl, { cache: 'no-store' });
                        const blob = await resp.blob();
                        const reader = new FileReader();
                        const dataUrl = await new Promise((res, rej) => {
                          reader.onloadend = () => res(reader.result);
                          reader.onerror = rej;
                          reader.readAsDataURL(blob);
                        });
                        return { logoDataUrl: dataUrl };
                      } catch {
                        return { logoDataUrl: null };
                      }
                    }}
                    buildDoc={(payload) =>
                      buildListenersDoc({
                        t, shortcode, stationName,
                        serviceName: t('listeners.title'),
                        startDate, endDate, startTime, endTime,
                        country, rows, total, fmtNumber, locale,
                        logoDataUrl: payload?.logoDataUrl || null, // ← передаём логотип в билдер
                      })
                    }
                    buildWorkbook={(XLSX, payload) =>
                      buildListenersWorkbook(XLSX, {
                        t, shortcode, stationName,
                        serviceName: t('listeners.title'),
                        startDate, endDate, startTime, endTime,
                        country, rows, total, fmtNumber, locale,
                        logoDataUrl: payload?.logoDataUrl || null
                      })
                    }
                    filename={`listeners_${shortcode}_${startDate}_${endDate}.pdf`}
                    xlsxFilename={`listeners_${shortcode}_${startDate}_${endDate}.xlsx`}
                    label={t('export_pdf')}
                    labelXlsx={t('export_excel')}
                    style={{ ...showBtn, background: (!rows.length || loading) ? '#9bbdfd' : '#3867fa' }}
                    disabled={!rows.length || loading}
                  />
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </form>

      {loading && <div>{t('common.loading')}</div>}
      {error && !loading && <div style={{color:'red', fontWeight:500, margin:"18px 0"}}>{error}</div>}
      {!loading && !error && total !== null && (
          <div style={{
            textAlign: 'right',
            fontSize: '1.2em',
            fontWeight: 'bold',
            padding: '0.4em',
            border: '1px solid #333',
            marginBottom: '1em',
            marginTop: '1ем',
            borderRadius: 7,
            background: '#fafafa'
          }}>
            {t('listeners.total', { count: total ?? 0, value: fmtNumber(total ?? 0, locale) })}
          </div>
      )}
      {!loading && !error && rows.length > 0 &&
        <table className="generic-listeners-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95em' }}>
          <thead>
            <tr>
                  <th style={thTd}>🕒 {t('listeners.table.hour')}</th>
                  <th style={thTd}>👥 {t('listeners.table.listeners')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                <td style={thTd}>{row.period}</td>
                <td style={thTd}>{fmtNumber(row.total_listeners, locale)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      }
      {!loading && !error && rows.length === 0 && (
        <div>{t('listeners.no_data')}</div>
      )}
    </div>
  );
}

const widgetStyle = {
  maxWidth: 900,
  margin: '40px auto',
  background: '#fff',
  borderRadius: 14,
  padding: 24,
  boxShadow: '0 2px 12px 0 #0001'
};

const filtersFormStyle = {
  marginBottom: '1em',
  width: '100%'
};

const formTableStyle = {
  width: '60%',
  borderCollapse: 'collapse'
};

const formLabelCellStyle = {
  padding: '6px 4px 6px 0',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap'
};

const formControlCellStyle = {
  padding: '6px 0',
  verticalAlign: 'middle',
  width: 'auto'
};

const formSpacerCellStyle = {
  width: 4
};

const dateFieldWrapperStyle = {
  maxWidth: 195,
  marginRight: 30
};


const timeFieldWrapperStyle = {
  maxWidth: 120
};

const ACTION_BUTTON_WIDTH = 120;
const ACTION_BUTTON_HEIGHT = 44;
const ACTION_BUTTON_GAP = 12;

const buttonCellStyle = {
  padding: '6px 0',
  verticalAlign: 'middle',
  display: 'flex',
  justifyContent: 'flex-start'
};

const buttonWrapperStyle = {
  display: 'inline-flex',
  gap: ACTION_BUTTON_GAP,
  justifyContent: 'flex-start'
};

const fieldLabelStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  fontWeight: 600,
  color: '#1a1a1a',
  whiteSpace: 'nowrap',
  minWidth: 110,
  width: 'auto'
};

const controlStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 8,
  border: '1px solid #ccc',
  fontSize: 16,
  boxSizing: 'border-box'
};

const showBtn = {
  background: '#3867fa',
  color: '#fff',
  border: 'none',
  padding: '10px 16px',
  borderRadius: 8,
  fontWeight: 600,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  width: ACTION_BUTTON_WIDTH,
  minWidth: ACTION_BUTTON_WIDTH,
  height: ACTION_BUTTON_HEIGHT,
  minHeight: ACTION_BUTTON_HEIGHT,
  whiteSpace: 'nowrap',
  transition: 'background 0.2s ease'
};

const thTd = {
  border: "1px solid #ddd",
  padding: 4,
  textAlign: "left",
  verticalAlign: "top"
};
